import React, { useEffect, useState } from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import Typography from '@mui/material/Typography';
import ChangelogEntryCard from '../../../../../components/changelog/ChangelogEntryCard';
import changelogStyles from '../../../../../components/changelog/Changelog.module.css';
import useItemEmailPreview from '../../hooks/useItemEmailPreview';

const toggleSx = { '& .MuiToggleButton-root': { textTransform: 'none', fontSize: '0.8125rem', py: 0.5, px: 1.5 } };

// Item do Admin no formato da página Novidades (ChangelogEntryCard)
const toEntry = (item) => ({
  id: item.slug,
  date: item.item_date,
  type: item.type,
  title: item.title || 'Sem título',
  summary: item.summary,
  area: item.area,
  link: item.link,
  cta: item.cta,
  plan: item.plan,
  image: item.image?.url || null,
  video: null,
});

function AppPreview({ item, highlight }) {
  const entry = toEntry(item);
  // O botão da tela só aparece com onOpenScreen; na prévia ele não navega
  const card = <ChangelogEntryCard entry={entry} highlight={highlight} showPlan onOpenScreen={() => {}} />;
  return (
    <Box sx={{ p: 3 }}>
      {highlight ? card : <div className={changelogStyles.entryGrid}>{card}</div>}
      {highlight && !entry.image && (
        <Alert severity="info" sx={{ mt: 2 }}>Destaque sem imagem: o cartão sai maior, mas sem ilustração.</Alert>
      )}
      {!highlight && item.type === 'fix' && (
        <Typography variant="caption" color="text.secondary" component="p" sx={{ mt: 2 }}>
          Na página, correções fora de destaque ficam recolhidas em &quot;N correções nesta semana&quot;.
        </Typography>
      )}
    </Box>
  );
}

function EmailPreview({ loading, error, html }) {
  if (loading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress size={32} /></Box>;
  }
  if (error) return <Alert severity="error" sx={{ m: 2 }}>{error}</Alert>;
  if (!html) return null;
  return (
    <iframe
      title="Prévia do e-mail da novidade"
      srcDoc={html}
      sandbox="allow-popups allow-popups-to-escape-sandbox"
      style={{ width: '100%', height: '65vh', border: 0, display: 'block', backgroundColor: '#FFFFFF', colorScheme: 'light' }}
    />
  );
}

/**
 * Prévia de uma novidade como o cliente vê: no app (cartão da página Novidades) ou no e-mail,
 * respeitando o destaque atual do item (destaque com imagem ou na lista).
 * @param {{ item: Object|null, onClose: Function }} props - item null = fechado
 */
function ItemPreviewDialog({ item, onClose }) {
  const open = Boolean(item);
  const [view, setView] = useState('app');
  const highlight = Boolean(Number(item?.highlight));

  useEffect(() => {
    if (item) setView('app');
  }, [item]);

  const email = useItemEmailPreview({ itemId: item?.id, highlight, enabled: open && view === 'email' });

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle sx={{ pb: 1 }}>
        <Typography variant="caption" color="text.secondary" component="div">Prévia da novidade</Typography>
        <Typography variant="subtitle1" component="div" sx={{ fontWeight: 600 }}>{item?.title || 'Sem título'}</Typography>
        <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', mt: 1.5 }}>
          <ToggleButtonGroup exclusive size="small" value={view} onChange={(_, v) => v && setView(v)} sx={toggleSx}>
            <ToggleButton value="app">No app</ToggleButton>
            <ToggleButton value="email">No e-mail</ToggleButton>
          </ToggleButtonGroup>
          <Typography variant="caption" color="text.secondary" sx={{ alignSelf: 'center' }}>
            {highlight ? 'Destaque, com imagem' : 'Na lista, sem destaque'}
          </Typography>
        </Box>
      </DialogTitle>
      <DialogContent dividers sx={{ p: 0, backgroundColor: '#F8F9FD' }}>
        {item && (view === 'app' ? <AppPreview item={item} highlight={highlight} /> : <EmailPreview {...email} />)}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Fechar</Button>
      </DialogActions>
    </Dialog>
  );
}

export default ItemPreviewDialog;
